import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { Clock, ArrowLeft, ArrowRight, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import Seo, { buildArticleSchema } from "@/components/Seo";
import { usePublicConfig } from "@/lib/usePublicConfig";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState("");
  const cfg = usePublicConfig();
  const relatedEnabled = cfg?.related_posts_enabled !== false; // default ON

  useEffect(() => {
    setPost(null);
    setRelated([]);
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setError("Artikel niet gevonden"));
  }, [slug]);

  useEffect(() => {
    if (!post || !relatedEnabled) return;
    api.get(`/blog/${slug}/related`, { params: { limit: 3 } })
      .then((r) => setRelated(r.data || []))
      .catch(() => setRelated([]));
  }, [post, slug, relatedEnabled]);

  return (
    <PublicLayout>
      {post && (
        <Seo
          title={post.title}
          description={post.excerpt}
          image={post.thumbnail}
          type="article"
          jsonLd={buildArticleSchema(post, slug)}
        />
      )}
      <section className="oc-section">
        <div className="oc-container max-w-3xl">
          <Link to="/blog" className="inline-flex items-center gap-2 text-oc-textSecondary hover:text-oc-primary text-[14px]">
            <ArrowLeft className="w-4 h-4" /> Terug naar blog
          </Link>

          {error && <p className="mt-10 text-center text-oc-textSecondary">{error}</p>}

          {post && (
            <article>
              <div className="mt-8">
                <span className="text-[12px] uppercase tracking-[0.1em] font-semibold text-oc-secondary">{post.category}</span>
                <h1 className="font-display font-bold text-[36px] sm:text-[48px] leading-tight tracking-tight text-oc-primary mt-3">
                  {post.title}
                </h1>
                <p className="mt-3 text-oc-textSecondary text-[14px] flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5" /> {post.read_time} min · {new Date(post.created_at).toLocaleDateString("nl-BE")}
                </p>
              </div>
              {post.thumbnail && (
                <img src={post.thumbnail} alt={post.title} className="mt-9 w-full aspect-[16/9] object-cover rounded-oc border border-oc-border" />
              )}
              <div
                className="prose prose-lg max-w-none mt-10 text-oc-textPrimary
                  [&_h2]:font-display [&_h2]:font-bold [&_h2]:text-[26px] [&_h2]:text-oc-primary [&_h2]:mt-10 [&_h2]:mb-3
                  [&_p]:leading-[1.8] [&_p]:my-4
                  [&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:my-2
                  [&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              <div className="mt-14 bg-oc-subtle border border-oc-border rounded-oc p-8 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="font-display font-semibold text-oc-primary text-[18px]">Ontvang je offerte al?</p>
                  <p className="text-oc-textSecondary text-[14px]">Check hem nu in 60 seconden voor €4,99.</p>
                </div>
                <Link to="/analyse">
                  <Button className="bg-oc-primary hover:bg-oc-secondary text-white rounded-oc-btn h-12 px-7 font-semibold">
                    Start mijn analyse <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>

              {related.length > 0 && (
                <section data-testid="related-posts" className="mt-16 pt-12 border-t border-oc-border">
                  <div className="flex items-center gap-3 mb-6">
                    <BookOpen className="w-5 h-5 text-oc-secondary" />
                    <h2 className="font-display font-bold text-[24px] text-oc-primary">Verder lezen</h2>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-5">
                    {related.map((r) => (
                      <Link
                        key={r.slug}
                        to={`/blog/${r.slug}`}
                        className="group bg-white border border-oc-border rounded-oc overflow-hidden hover:shadow-oc-card-hover transition-shadow"
                      >
                        {r.thumbnail ? (
                          <img src={r.thumbnail} alt={r.title} className="w-full aspect-[16/9] object-cover" />
                        ) : (
                          <div className="w-full aspect-[16/9] bg-gradient-to-br from-oc-primary/10 to-oc-secondary/10" />
                        )}
                        <div className="p-5">
                          {r.category && (
                            <span className="text-[11px] uppercase tracking-[0.08em] font-semibold text-oc-secondary">
                              {r.category}
                            </span>
                          )}
                          <h3 className="mt-1.5 font-display font-semibold text-oc-primary text-[16px] leading-snug group-hover:text-oc-secondary line-clamp-2">
                            {r.title}
                          </h3>
                          <p className="mt-2 text-oc-textSecondary text-[12px] inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {r.read_time} min
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </article>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
